/**
 * Non-component helpers behind the shared SMS-thread panes (SmsThreadView.tsx):
 * the per-media card-image fetch hook + small formatters. Split from the
 * component file so fast refresh keeps working there.
 */
import { useEffect, useRef, useState } from 'react'
import { pickCaseCardMedia } from '../../shared/recoveryDrawer'
import type { RecoveryMessage } from '../services/recoveryCases'

export type CardUrls = Record<string, string>
export const mediaKey = (intakeId: string, objectIndex: number) => `${intakeId}:${objectIndex}`

export function fmtDob(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  return m ? `${m[2]}/${m[3]}/${m[1]}` : iso
}

/**
 * A thread message's timestamp, e.g. `Jun 26 · 9:02 AM` — the prototype's shape.
 *
 * Locale is pinned to en-US, like every other formatter in the app: the bare
 * `toLocaleString()` follows the BROWSER's locale, so the same thread rendered
 * "20 июл., 6:47" on one machine and "Jul 20, 6:47 AM" on another. This is a
 * US-market product whose staff read US dates, and a thread is evidence of what
 * we told a patient and when — it must read identically wherever it's opened.
 */
export function fmtMsgTime(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  const day = d.toLocaleDateString('en-US', {month: 'short', day: 'numeric'})
  const time = d.toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', hour12: true,
  })
  return `${day} · ${time}`
}

/**
 * Fetch each inbound card image once (base64 → data URL; the backend never
 * mints a URL, so nothing is fetchable outside the callable). A failed fetch
 * drops its key so a later messages change retries. `fetchImage` must be
 * referentially stable (useCallback in the caller).
 */
export function useCardImageUrls(
  messages: RecoveryMessage[],
  fetchImage: (media: { intakeId: string; objectIndex: number }) =>
    Promise<{ data: string; contentType: string }>,
): CardUrls {
  const [cardUrls, setCardUrls] = useState<CardUrls>({})
  const requestedRef = useRef<Set<string>>(new Set())
  const mountedRef = useRef(true)
  // Re-armed on every mount, not just cleared on unmount. StrictMode (on in
  // main.tsx) tears every effect down once immediately after mounting it, so a
  // clear-only cleanup latched the ref `false` before any fetch resolved —
  // every `setCardUrls` was skipped, `requestedRef` already held the key so
  // nothing retried, and no card image ever rendered on a dev build. Found on
  // #318 review; the prod build (no StrictMode) never showed it.
  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  useEffect(() => {
    for (const m of messages) {
      if (m.direction !== 'inbound') continue
      for (const media of m.media) {
        const key = mediaKey(media.intakeId, media.objectIndex)
        if (requestedRef.current.has(key)) continue
        requestedRef.current.add(key)
        fetchImage(media)
          .then((res) => {
            if (!mountedRef.current) return
            setCardUrls((prev) => ({
              ...prev,
              [key]: `data:${res.contentType};base64,${res.data}`,
            }))
          })
          .catch(() => {
            // A failed image shouldn't blank the view — and dropping the key
            // lets a later messages update retry the fetch.
            requestedRef.current.delete(key)
          })
      }
    }
  }, [messages, fetchImage])

  return cardUrls
}

/** The newest inbound photo that has loaded — the Extraction pane's hero image
 *  when no case scopes the view (thread-level fallback). */
export function latestCardUrl(messages: RecoveryMessage[], cardUrls: CardUrls): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (m.direction === 'inbound' && m.media.length > 0) {
      const k = mediaKey(m.media[0].intakeId, m.media[0].objectIndex)
      if (cardUrls[k]) return cardUrls[k]
    }
  }
  return null
}

/**
 * The newest loaded photo belonging to ONE case (#279) — the Extraction pane's
 * hero image when a case is open. Photos attributed to this case win; a thread
 * with none falls back to unattributed photos only (pending attribution or
 * legacy), never a photo already attributed to a different case — so a newer
 * photo elsewhere on a shared/repeat phone can't replace an older case's image.
 */
/** One card image belonging to a case, with what we know about it (#309). */
export interface CaseCardImage {
  url: string
  intakeId: string
  objectIndex: number
  /** When the message carrying it arrived — the only provenance the pane can
   *  state without guessing which SIDE of a card an image is. */
  receivedAt: string | null
}

/**
 * EVERY loaded image attributed to a case, newest first (#309).
 *
 * The pane used to consume only the first entry of this same ordered list. When
 * a patient sends front and back, that meant showing one of them — routinely
 * the back — beside a member ID the displayed image could not contain. A
 * verification surface that withholds the evidence trains people not to verify.
 */
export function caseCardImages(
  messages: RecoveryMessage[],
  cardUrls: CardUrls,
  caseId: string,
): CaseCardImage[] {
  const receivedByIntake = new Map<string, string | null>()
  for (const m of messages) {
    for (const media of m.media) {
      receivedByIntake.set(media.intakeId, m.occurredAt ?? null)
    }
  }
  const out: CaseCardImage[] = []
  for (const media of pickCaseCardMedia(messages, caseId)) {
    const url = cardUrls[mediaKey(media.intakeId, media.objectIndex)]
    if (!url) continue
    out.push({
      url,
      intakeId: media.intakeId,
      objectIndex: media.objectIndex,
      receivedAt: receivedByIntake.get(media.intakeId) ?? null,
    })
  }
  return out
}

export function caseCardUrl(
  messages: RecoveryMessage[],
  cardUrls: CardUrls,
  caseId: string,
): string | null {
  for (const media of pickCaseCardMedia(messages, caseId)) {
    const url = cardUrls[mediaKey(media.intakeId, media.objectIndex)]
    if (url) return url
  }
  return null
}
